// EmbedBlockEditor — arbitrary iframe HTML (admin-trusted).
//
// The textarea accepts raw HTML; the preview renders it inside a
// sandboxed shadow container — but only when the block is *not*
// selected, to avoid distracting the editor while typing.
//
// NOTE: This is intentionally raw — embeds from Twitter, SoundCloud,
// CodePen etc. all use proprietary iframe markup. We trust the admin user
// to paste safe HTML; the published renderer can sanitize further.

import type { EmbedBlock, Block } from "../../../lib/blocks";
import { t } from "../theme";

export interface EmbedBlockEditorProps {
  block: EmbedBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

export function EmbedBlockEditor({
  block,
  onChange,
  isSelected,
}: EmbedBlockEditorProps) {
  return (
    <div>
      <textarea
        value={block.html}
        onChange={(e) => onChange({ html: e.target.value } as Partial<EmbedBlock>)}
        placeholder='<iframe src="..." />'
        rows={Math.min(8, Math.max(3, block.html.split("\n").length + 1))}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: 10,
          background: t.bg,
          border: `1px solid ${t.border}`,
          borderRadius: 6,
          fontFamily: t.mono,
          fontSize: 12,
          color: t.text,
          outline: "none",
          resize: "vertical",
        }}
      />

      {!isSelected && block.html.trim() && (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            border: `1px solid ${t.border}`,
            borderRadius: 6,
            background: t.card,
          }}
          // Admin-trusted embed preview.
          dangerouslySetInnerHTML={{ __html: block.html }}
        />
      )}

      {isSelected && (
        <input
          type="text"
          value={block.caption || ""}
          onChange={(e) => onChange({ caption: e.target.value } as Partial<EmbedBlock>)}
          placeholder="Caption (optional)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 8,
            padding: "6px 8px",
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 4,
            fontSize: 12,
            color: t.text,
            outline: "none",
            fontFamily: t.sans,
          }}
        />
      )}
    </div>
  );
}
